import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isDate, format, parseISO, isValid } from 'date-fns';
import { zonedTimeToUtc, toDate, format as tzFormat } from 'date-fns-tz';
import isEmpty from 'lodash/isEmpty';
import { catchError, Observable, tap } from 'rxjs';

import { labelMinLength, labelMaxLength } from '../../app.constants';
import { ConfigService } from '../../config.service';
import { FwLegacyDialogService as DialogService } from '../../dialogs/dialog.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { QueriesDialogComponent } from '../../queries/queries-dialog/queries-dialog.component';
import { SessionService } from '../session.service';
import { ageInYears, timestampToLocaltime, TIMEZONES } from '../../shared/date.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { numberInteger, validDate } from '../../shared/form.utils';
import { Option } from '../../shared/models/option.model';
import { Session } from '../../shared/models/session.model';
import { ChangeManagerService } from 'app/src/change-manager.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FwIconModule, FwTooltipModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';
import { ProjectsService } from 'app/src/projects/projects.service';
import { CoreApiService } from 'app/src/core-api.service';
import { FwTimeFieldComponent } from 'app/src/shared/components/fw-time-field/fw-time-field.component';

@Component({
  host: {
    'test-id': 'session-info',
  },
  selector: 'session-info',
  templateUrl: './session-info.component.html',
  styleUrls: ['./session-info.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwIconModule,
    FwTooltipModule,
    FwTimeFieldComponent,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
  providers: [
    ProjectsService,
    CoreApiService,
  ],
})
export class SessionInfoComponent implements OnChanges {
  @Input() session: Session
  @Input('permissions') permissionSet = new Set<PermissionAction>()

  @Output() afterSave = new EventEmitter<Session>()

  metadata = new FormGroup({
    label: new FormControl('', [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(labelMaxLength)]),
    timestampDate: new FormControl(null, [validDate]),
    timestampTime: new FormControl(null, [Validators.required]),
    timezone: new FormControl(''),
    age: new FormControl(null, [Validators.min(0), numberInteger]),
    weight: new FormControl(null, [Validators.min(0)]),
    operator: new FormControl(''),
  })
  permissions = {
    editMetadata: false,
    deleteContainer: false,
    viewAuditTrail: false,
  }
  timezoneOptions: Option[] = TIMEZONES.map(tz => ({ label: tz, value: tz }))

  constructor(
    private changeManager: ChangeManagerService,
    private config: ConfigService,
    private dialog: DialogService,
    private logger: LoggingService,
    private notifications: NotificationService,
    public sessionService: SessionService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permissionSet) {
      this.permissions = {
        editMetadata: this.permissionSet.has(PermissionAction.ContainersModifyMetadata),
        deleteContainer: this.permissionSet.has(PermissionAction.ContainersDeleteHierarchy),
        viewAuditTrail: this.permissionSet.has(PermissionAction.ContainersViewMetadata) && this.config.isFeatureEnabled('audit_trail'),
      };
      if (this.permissions.editMetadata) {
        this.changeManager.registerForm('SessionInfoComponent', {
          control: this.metadata,
          reset: () => this.resetForm(),
          save: () => this._saveChanges(),
          successMessageKey: 'session.saveSession.success',
          errorMessageKey: 'session.saveSession.error',
        });
      }
    }
    if (changes.session && this.session?._id
      && (this.metadata.pristine || changes.session.firstChange || this.session?._id !== changes.session.previousValue?._id)) {
      this.resetForm();
    }
  }

  ngOnDestroy(): void {
    this.changeManager.deregisterForm('SessionInfoComponent');
  }

  get ageInYears(): number {
    return ageInYears(this.session?.age);
  }

  get timestamp(): string {
    return this.session.timestamp ? this.sessionService.formattedTimestamp(this.session) : 'No Timestamp';
  }

  get timezone(): string {
    return this.metadata.get('timezone').value;
  }

  get localSessionTimestamp(): string {
    if (!this.session.timestamp) {
      return 'No timestamp';
    }
    if (!this.session.timezone && !this.timezone) {
      return 'No timezone saved for this timestamp';
    }
    const date = this.getFormDate();
    if (isDate(date)) {
      const utcDate = zonedTimeToUtc(date, isEmpty(this.timezone) ? 'UTC' : this.timezone);
      if (!isValid(utcDate)) {
        return 'Session timezone is invalid';
      }
      const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedDate = tzFormat(utcDate, 'yyyy-MM-dd HH:mm', { timeZone: localTimezone });
      return `Converted to ${localTimezone}: ${formattedDate}`;
    }
    return '';
  }

  resetForm(): void {
    const timezone = this.session.timezone;
    let timestampDate = null;
    if (this.sessionService.showTimezone$.value) {
      timestampDate = this.session.timestamp ? new Date(timestampToLocaltime(this.session.timestamp, timezone, 'yyyy-MM-dd HH:mm')) : null;
    } else {
      timestampDate = this.session.timestamp ? parseISO(this.session.timestamp) : null;
    }

    const timestampTime = format(timestampDate || new Date(), 'HH:mm');
    this.metadata.reset({
      label: this.session.label || '',
      timestampDate: timestampDate,
      timestampTime: timestampTime,
      timezone: timezone,
      age: ageInYears(this.session.age),
      weight: this.session.weight || null,
      operator: this.session.operator || '',
    });
    this.metadata.markAsPristine(); // reset should do this, but does not
    this.metadata.updateValueAndValidity(); // emit pristine state
  }

  private _saveChanges(): Observable<void> {
    const form = this.metadata.value;
    const payload: Partial<Session> = {
      label: form.label,
      weight: form.weight,
      operator: form.operator,
    };
    const { timestampDate, timestampTime } = this.metadata.controls;
    if (timestampDate.dirty || timestampTime.dirty || this.timezone !== this.session.timezone) {
      // only send timestamp if changed to preserve precision
      const date = this.getFormDate();
      if (isDate(date)) {
        let utcDate = null;
        if (this.sessionService.showTimezone$.value) {
          utcDate = zonedTimeToUtc(date, this.timezone || 'UTC');
        }
        else {
          utcDate = toDate(date, { timeZone: 'UTC' });
        }
        payload.timestamp = utcDate.toISOString();
      } else {
        payload.timestamp = null;
      }
    }
    if (form.age !== this.ageInYears) {
      // only send age if changed to preserve precision
      payload.age = form.age ? form.age * 60 * 60 * 24 * 365.25 : form.age;
    }
    if (this.timezone !== this.session.timezone) {
      payload.timezone = this.timezone;
    }
    return this.sessionService.saveSession(this.session._id, payload).pipe(
      tap(() => {
        this.metadata.markAsPristine();
        this.afterSave.emit({ ...this.session, ...payload });
      }),
      catchError(error => {
        this.logger.error(error);
        throw error;
      }),
    );
  }

  saveChanges(): void {
    this._saveChanges().subscribe({
      next: () => {
        this.notifications.success('session.saveSession.success');
      },
      error: () => {
        this.notifications.error('session.saveSession.error');
      },
    });
  }

  deleteSession(): void {
    this.sessionService.deleteSession(this.session).subscribe();
  }

  getFormDate(): Date | null {
    if (!this.metadata) {
      return null;
    }
    const form = this.metadata.value;
    if (isDate(form.timestampDate)) {
      const date = toDate(form.timestampDate); // copy instance
      const [hours, minutes] = form.timestampTime.split(':');
      date.setHours(hours, minutes, 0, 0);
      return date;
    }
    return null;
  }

  viewAuditTrail(): void {
    this.dialog.open(QueriesDialogComponent, {
      hasBackdrop: true,
      disableClose: false,
      height: '100%',
      width: '850px',
      panelClass: ['border-radius-0', 'padding-0'],
      position: { right: '0' },
      data: {
        auditable: this.session,
        type: 'object',
      },
    });
  }
}
